// ─── PLATFORM BACKUP (Sara's centralized Airtable Swap) ──────────────────────
// Super-admin only. 4 sections:
//   1. Connexion Airtable Swap
//   2. Mapping Cabinet → Workspace
//   3. Import historique
//   4. Logs
//
// Endpoints under /api/admin/platform-backup/*

import React, { useState, useEffect } from 'react';
import api from '../services/api';

// Reuse the same dark theme tokens as Admin.js
const D = {
  bg:'#0A0A0F', s1:'#0F0F17', s2:'#15151F', s3:'#1A1A26',
  b1:'rgba(255,255,255,0.06)', b2:'rgba(255,255,255,0.12)',
  tx:'#E8E6E0', tx2:'rgba(232,230,224,0.65)', tx3:'rgba(232,230,224,0.4)',
  or:'#D4A843', orHi:'#E8BC5A',
  green:'#4FBF7F', red:'#EF5B5B', blue:'#5B8DEF', amber:'#F59E0B',
};

const card  = { background:D.s2, border:`1px solid ${D.b1}`, borderRadius:'8px' };
const inp   = { width:'100%', padding:'7px 11px', background:D.s2, border:`1px solid ${D.b1}`, borderRadius:'7px', fontSize:'12px', color:D.tx, outline:'none', fontFamily:'DM Sans, sans-serif' };
const inpSm = { ...inp, padding:'5px 9px', fontSize:'11px' };
const btn   = { background:D.or, color:'#09090E', border:'none', padding:'7px 16px', borderRadius:'6px', fontSize:'11px', fontWeight:500, cursor:'pointer', fontFamily:'DM Sans, sans-serif' };
const btnGh = { background:'transparent', color:D.tx2, border:`1px solid ${D.b1}`, padding:'7px 14px', borderRadius:'6px', fontSize:'11px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' };
const btnDg = { background:'transparent', color:D.red, border:`1px solid rgba(239,91,91,0.3)`, padding:'5px 10px', borderRadius:'5px', fontSize:'10px', cursor:'pointer', fontFamily:'DM Sans, sans-serif' };
const lbl   = { fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.05em', color:D.tx3, marginBottom:'4px' };
const sectionTitle = { fontFamily:'Syne, sans-serif', fontSize:'14px', fontWeight:600, color:D.tx, marginBottom:'12px' };

function Toast({ msg, type }) {
  if (!msg) return null;
  const c = type === 'error' ? D.red : type === 'warning' ? D.amber : D.green;
  return (
    <div style={{ position:'fixed', bottom:'20px', right:'20px', background:D.s3, border:`1px solid ${c}`, color:c, padding:'10px 16px', borderRadius:'6px', fontSize:'12px', zIndex:1000 }}>
      {msg}
    </div>
  );
}

export default function PlatformBackup() {
  const [toast, setToast] = useState(null);
  const showToast = (msg, type='success') => { setToast({msg,type}); setTimeout(()=>setToast(null), 3500); };

  return (
    <div>
      <BackupConfigSection showToast={showToast} />
      <div style={{ height:'16px' }} />
      <CabinetMappingsSection showToast={showToast} />
      <div style={{ height:'16px' }} />
      <ImportSection showToast={showToast} />
      <div style={{ height:'16px' }} />
      <LogsSection />
      <Toast msg={toast?.msg} type={toast?.type} />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// SECTION 1 — Configuration de la base Swap
// ═════════════════════════════════════════════════════════════════════════════

function BackupConfigSection({ showToast }) {
  const [cfg, setCfg]         = useState(null);
  const [loading, setLoading] = useState(true);
  const [editPat, setEditPat] = useState(false);
  const [form, setForm]       = useState({ api_key:'', base_id:'', table_id:'', cabinet_column:'Cabinet', is_active:true, field_mapping:{} });
  const [tables, setTables]   = useState([]);
  const [columns, setColumns] = useState([]);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [testOk, setTestOk]   = useState(false);

  useEffect(() => {
    api.get('/api/admin/platform-backup/config')
      .then(r => {
        setCfg(r.data || {});
        if (r.data?.id) {
          setForm({
            api_key:'',
            base_id: r.data.base_id || '',
            table_id: r.data.table_id || '',
            cabinet_column: r.data.cabinet_column || 'Cabinet',
            is_active: r.data.is_active !== false,
            field_mapping: r.data.field_mapping || {},
          });
          setTestOk(true);
        }
      })
      .catch(()=>{})
      .finally(()=>setLoading(false));
  }, []);

  const isConfigured = !!cfg?.id && !!cfg?.api_key_masked;

  const handleTest = async () => {
    if (!form.api_key.trim() || !form.base_id) { showToast('PAT et Base ID requis', 'error'); return; }
    setTesting(true);
    try {
      const r = await api.post('/api/admin/platform-backup/config/test', { api_key: form.api_key.trim(), base_id: form.base_id });
      if (r.data?.success) {
        setTables(r.data.tables || []);
        setTestOk(true);
        showToast(`Connexion OK — ${r.data.tables?.length || 0} table(s)`);
        if (r.data.tables?.length === 1) {
          setForm(f => ({...f, table_id: r.data.tables[0].id}));
          setColumns(r.data.tables[0].fields || []);
        } else if (form.table_id) {
          const t = r.data.tables.find(t => t.id === form.table_id);
          if (t) setColumns(t.fields || []);
        }
      } else {
        showToast(r.data?.error || 'Erreur de connexion', 'error');
      }
    } catch(e) {
      showToast(e.response?.data?.detail || 'Erreur réseau', 'error');
    } finally { setTesting(false); }
  };

  const handleSave = async () => {
    if (!form.base_id || !form.table_id) { showToast('Base ID et Table requis', 'error'); return; }
    setSaving(true);
    try {
      const payload = {
        base_id: form.base_id,
        table_id: form.table_id,
        cabinet_column: form.cabinet_column,
        is_active: form.is_active,
        field_mapping: form.field_mapping,
      };
      if (form.api_key.trim()) payload.api_key = form.api_key.trim();
      const r = await api.post('/api/admin/platform-backup/config', payload);
      setCfg(r.data);
      setEditPat(false);
      setForm(f => ({...f, api_key:''}));
      showToast('Configuration sauvegardée');
    } catch(e) {
      showToast(e.response?.data?.detail || 'Erreur', 'error');
    } finally { setSaving(false); }
  };

  if (loading) return <div style={{ ...card, padding:'20px', color:D.tx3, fontSize:'12px' }}>Chargement…</div>;

  return (
    <div style={{ ...card, padding:'18px' }}>
      <div style={sectionTitle}>
        Connexion Airtable Swap
        {isConfigured && <span style={{ marginLeft:'10px', fontSize:'10px', color:D.green, background:'rgba(79,191,127,0.1)', border:`1px solid rgba(79,191,127,0.3)`, padding:'2px 8px', borderRadius:'4px', fontWeight:500 }}>✓ Connecté</span>}
        {cfg?.last_push_at && <span style={{ marginLeft:'8px', fontSize:'10px', color:D.tx3 }}>{cfg.push_count || 0} pushs · dernier {new Date(cfg.last_push_at).toLocaleString('fr-FR')}</span>}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'10px' }}>
        <div>
          <div style={lbl}>Personal Access Token</div>
          {isConfigured && !editPat ? (
            <div style={{ ...inp, display:'flex', alignItems:'center', justifyContent:'space-between', color:D.tx3, fontFamily:'monospace' }}>
              <span>pat••••••••••••••••</span>
              <button onClick={() => { setEditPat(true); setTestOk(false); }} style={{ background:'none', border:'none', color:D.or, fontSize:'10px', cursor:'pointer', textDecoration:'underline' }}>Modifier</button>
            </div>
          ) : (
            <input style={inp} type="password" placeholder="pat..." value={form.api_key} onChange={e => setForm({...form, api_key:e.target.value})} />
          )}
        </div>
        <div>
          <div style={lbl}>Base ID</div>
          <input style={inp} placeholder="appXXXXXXXXXXXXXX" value={form.base_id} onChange={e => { setForm({...form, base_id:e.target.value}); setTestOk(false); }} />
        </div>
      </div>

      <div style={{ display:'flex', gap:'8px', marginBottom:'12px' }}>
        <button onClick={handleTest} disabled={testing || (!form.api_key.trim() && !isConfigured) || !form.base_id} style={{ ...btn, opacity: (testing||(!form.api_key.trim() && !isConfigured)||!form.base_id) ? 0.5 : 1 }}>
          {testing ? 'Test…' : 'Tester la connexion'}
        </button>
        {testOk && <span style={{ fontSize:'11px', color:D.green, alignSelf:'center' }}>✓ Connexion OK</span>}
      </div>

      {(testOk || isConfigured) && tables.length > 0 && (
        <div style={{ marginBottom:'10px' }}>
          <div style={lbl}>Table cible</div>
          <select style={inp} value={form.table_id} onChange={e => {
            setForm({...form, table_id:e.target.value});
            const t = tables.find(t => t.id === e.target.value);
            setColumns(t ? (t.fields || []) : []);
          }}>
            <option value="">— Sélectionner —</option>
            {tables.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
      )}

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'12px' }}>
        <div>
          <div style={lbl}>Colonne "Cabinet" (pour dispatcher)</div>
          <input style={inp} placeholder="Cabinet" value={form.cabinet_column} onChange={e => setForm({...form, cabinet_column:e.target.value})} />
        </div>
        <div>
          <div style={lbl}>Activation</div>
          <label style={{ ...inp, display:'flex', alignItems:'center', gap:'8px', cursor:'pointer' }}>
            <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active:e.target.checked})} />
            <span style={{ color:D.tx2 }}>Push activé sur toutes les modifs de dossier</span>
          </label>
        </div>
      </div>

      <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end' }}>
        <button onClick={handleSave} disabled={saving || !form.base_id || !form.table_id} style={{ ...btn, opacity: (saving||!form.base_id||!form.table_id) ? 0.5 : 1 }}>
          {saving ? 'Sauvegarde…' : 'Sauvegarder'}
        </button>
      </div>

      {/* Mapping des champs */}
      {isConfigured && (
        <MappingCopyBlock cfg={cfg} reloadCfg={() => {
          api.get('/api/admin/platform-backup/config').then(r => setCfg(r.data || {}));
        }} />
      )}

      {cfg?.last_error && (
        <div style={{ marginTop:'10px', background:'rgba(239,91,91,0.06)', border:`1px solid rgba(239,91,91,0.2)`, borderRadius:'6px', padding:'8px 12px', fontSize:'11px', color:D.red }}>
          Dernière erreur : {cfg.last_error}
        </div>
      )}
    </div>
  );
}


function MappingCopyBlock({ cfg, reloadCfg }) {
  const [workspaces, setWorkspaces] = useState([]);
  const [selectedWs, setSelectedWs]  = useState('');
  const [copying, setCopying]        = useState(false);
  const [feedback, setFeedback]      = useState(null);

  useEffect(() => {
    api.get('/api/admin/workspaces').then(r => setWorkspaces(r.data || [])).catch(()=>{});
  }, []);

  const fieldsCount = cfg?.field_mapping ? Object.keys(cfg.field_mapping).length : 0;

  const handleCopy = async () => {
    if (!selectedWs) return;
    if (fieldsCount > 0 && !window.confirm(`Le mapping actuel (${fieldsCount} champs) sera remplacé. Continuer ?`)) return;
    setCopying(true);
    try {
      const r = await api.post('/api/admin/platform-backup/config/copy-mapping', { workspace_id: selectedWs });
      setFeedback({ type: 'success', msg: `${r.data.fields_copied} champ(s) copié(s)` });
      reloadCfg();
      setTimeout(() => setFeedback(null), 4000);
    } catch (e) {
      setFeedback({ type: 'error', msg: e.response?.data?.detail || 'Erreur' });
    } finally {
      setCopying(false);
    }
  };

  return (
    <div style={{ marginTop:'18px', padding:'14px', background:D.s3, border:`1px solid ${D.b1}`, borderRadius:'7px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'10px' }}>
        <div>
          <div style={{ fontSize:'12px', color:D.tx, fontWeight:500 }}>Mapping des champs</div>
          <div style={{ fontSize:'10px', color:D.tx3, marginTop:'2px' }}>
            {fieldsCount > 0
              ? `${fieldsCount} champ(s) mappé(s) — push/import opérationnels`
              : 'Aucun mapping configuré — le push ne transmet aucune donnée'}
          </div>
        </div>
        <div style={{ fontSize:'10px', color: fieldsCount > 0 ? D.green : D.amber, fontWeight:500 }}>
          {fieldsCount > 0 ? '✓ OK' : '⚠ À configurer'}
        </div>
      </div>

      <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
        <select style={{ ...inp, flex:1 }} value={selectedWs} onChange={e => setSelectedWs(e.target.value)}>
          <option value="">Copier le mapping depuis un workspace…</option>
          {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        <button onClick={handleCopy} disabled={!selectedWs || copying} style={{ ...btn, opacity: (!selectedWs||copying) ? 0.5 : 1 }}>
          {copying ? '…' : 'Copier'}
        </button>
      </div>

      {feedback && (
        <div style={{
          marginTop:'8px', fontSize:'11px',
          color: feedback.type === 'success' ? D.green : D.red,
        }}>{feedback.msg}</div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// SECTION 2 — Mapping Cabinet → Workspace
// ═════════════════════════════════════════════════════════════════════════════

function CabinetMappingsSection({ showToast }) {
  const [mappings, setMappings]     = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [newRow, setNewRow]         = useState({ cabinet_name:'', workspace_id:'', notes:'' });
  const [editing, setEditing]       = useState({});  // { [id]: { cabinet_name, workspace_id, notes } }

  const refresh = () => {
    return Promise.all([
      api.get('/api/admin/platform-backup/mappings').then(r => setMappings(r.data || [])),
      api.get('/api/admin/workspaces').then(r => setWorkspaces(r.data || [])),
    ]).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const handleAdd = async () => {
    if (!newRow.cabinet_name.trim() || !newRow.workspace_id) {
      showToast('Cabinet et Workspace requis', 'error'); return;
    }
    try {
      await api.post('/api/admin/platform-backup/mappings', {
        cabinet_name: newRow.cabinet_name.trim(),
        workspace_id: newRow.workspace_id,
        notes: newRow.notes || null,
      });
      setNewRow({ cabinet_name:'', workspace_id:'', notes:'' });
      refresh();
      showToast('Mapping créé');
    } catch(e) {
      showToast(e.response?.data?.detail || 'Erreur', 'error');
    }
  };

  const handleEdit = (id) => {
    const m = mappings.find(x => x.id === id);
    if (!m) return;
    setEditing({...editing, [id]: { cabinet_name:m.cabinet_name, workspace_id:m.workspace_id, notes:m.notes||'' }});
  };

  const handleSaveEdit = async (id) => {
    const data = editing[id];
    if (!data?.cabinet_name?.trim() || !data?.workspace_id) {
      showToast('Cabinet et Workspace requis', 'error'); return;
    }
    try {
      await api.patch(`/api/admin/platform-backup/mappings/${id}`, {
        cabinet_name: data.cabinet_name.trim(),
        workspace_id: data.workspace_id,
        notes: data.notes || null,
      });
      const next = {...editing};
      delete next[id];
      setEditing(next);
      refresh();
      showToast('Mapping modifié');
    } catch(e) {
      showToast(e.response?.data?.detail || 'Erreur', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce mapping ?')) return;
    try {
      await api.delete(`/api/admin/platform-backup/mappings/${id}`);
      refresh();
      showToast('Mapping supprimé');
    } catch(e) {
      showToast(e.response?.data?.detail || 'Erreur', 'error');
    }
  };

  if (loading) return <div style={{ ...card, padding:'20px', color:D.tx3, fontSize:'12px' }}>Chargement…</div>;

  return (
    <div style={{ ...card, padding:'18px' }}>
      <div style={sectionTitle}>
        Mapping Cabinets → Workspaces
        <span style={{ marginLeft:'10px', fontSize:'10px', color:D.tx3 }}>{mappings.length} mapping(s)</span>
      </div>
      <div style={{ fontSize:'11px', color:D.tx3, marginBottom:'12px' }}>
        Indique à quel workspace Lexback chaque cabinet (depuis ta colonne Airtable) doit être rattaché. Les cabinets inconnus créeront automatiquement un workspace (plan starter, status pending).
      </div>

      {/* Table existante */}
      {mappings.length > 0 && (
        <div style={{ marginBottom:'14px', border:`1px solid ${D.b1}`, borderRadius:'6px', overflow:'hidden' }}>
          <div style={{ display:'grid', gridTemplateColumns:'2fr 2fr 2fr 1fr auto', gap:'10px', padding:'8px 12px', background:D.s3, fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.05em', color:D.tx3, fontWeight:600 }}>
            <div>Cabinet</div>
            <div>Workspace</div>
            <div>Notes</div>
            <div>Créé</div>
            <div></div>
          </div>
          {mappings.map(m => {
            const ed = editing[m.id];
            const isEditing = !!ed;
            return (
              <div key={m.id} style={{ display:'grid', gridTemplateColumns:'2fr 2fr 2fr 1fr auto', gap:'10px', padding:'8px 12px', borderTop:`1px solid ${D.b1}`, alignItems:'center', fontSize:'11px' }}>
                {isEditing ? (
                  <>
                    <input style={inpSm} value={ed.cabinet_name} onChange={e => setEditing({...editing, [m.id]:{...ed, cabinet_name:e.target.value}})} />
                    <select style={inpSm} value={ed.workspace_id} onChange={e => setEditing({...editing, [m.id]:{...ed, workspace_id:e.target.value}})}>
                      {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                    <input style={inpSm} value={ed.notes} onChange={e => setEditing({...editing, [m.id]:{...ed, notes:e.target.value}})} />
                    <div style={{ color:D.tx3, fontSize:'10px' }}>{m.auto_created ? 'Auto' : 'Manuel'}</div>
                    <div style={{ display:'flex', gap:'4px' }}>
                      <button onClick={() => handleSaveEdit(m.id)} style={{ ...btn, padding:'4px 10px', fontSize:'10px' }}>OK</button>
                      <button onClick={() => { const n={...editing}; delete n[m.id]; setEditing(n); }} style={{ ...btnGh, padding:'4px 8px', fontSize:'10px' }}>✕</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ color:D.tx }}>{m.cabinet_name}</div>
                    <div style={{ color:D.tx2 }}>{m.workspace_name || <span style={{ color:D.red }}>(supprimé)</span>}</div>
                    <div style={{ color:D.tx3 }}>{m.notes || '—'}</div>
                    <div style={{ color:D.tx3, fontSize:'10px' }}>{m.auto_created ? 'Auto' : 'Manuel'}</div>
                    <div style={{ display:'flex', gap:'4px' }}>
                      <button onClick={() => handleEdit(m.id)} style={{ ...btnGh, padding:'4px 8px', fontSize:'10px' }}>Modifier</button>
                      <button onClick={() => handleDelete(m.id)} style={btnDg}>Suppr</button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Nouvelle ligne */}
      <div style={{ background:D.s3, border:`1px dashed ${D.b1}`, borderRadius:'6px', padding:'10px 12px' }}>
        <div style={{ fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.05em', color:D.tx3, marginBottom:'8px', fontWeight:600 }}>Ajouter un mapping</div>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 2fr 2fr auto', gap:'10px', alignItems:'center' }}>
          <input style={inpSm} placeholder="Nom du cabinet" value={newRow.cabinet_name} onChange={e => setNewRow({...newRow, cabinet_name:e.target.value})} />
          <select style={inpSm} value={newRow.workspace_id} onChange={e => setNewRow({...newRow, workspace_id:e.target.value})}>
            <option value="">— Choisir workspace —</option>
            {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
          <input style={inpSm} placeholder="Notes (optionnel)" value={newRow.notes} onChange={e => setNewRow({...newRow, notes:e.target.value})} />
          <button onClick={handleAdd} style={{ ...btn, padding:'5px 14px', fontSize:'10px' }}>+ Ajouter</button>
        </div>
      </div>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// SECTION 3 — Import historique
// ═════════════════════════════════════════════════════════════════════════════

function ImportSection({ showToast }) {
  const [importing, setImporting] = useState(false);
  const [tables, setTables]       = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading]     = useState(true);

  const [tableId, setTableId]     = useState('');
  const [targetWs, setTargetWs]   = useState('');  // '' = auto via Cabinet

  useEffect(() => {
    Promise.all([
      api.get('/api/admin/platform-backup/tables').then(r => r.data?.tables || []).catch(() => []),
      api.get('/api/admin/workspaces').then(r => r.data || []).catch(() => []),
    ]).then(([t, w]) => {
      setTables(t);
      setWorkspaces(w);
    }).finally(() => setLoading(false));
  }, []);

  const handleImport = async () => {
    if (!tableId) {
      showToast('Choisis une table à importer', 'error');
      return;
    }
    const wsLabel = targetWs
      ? `vers le workspace "${workspaces.find(w => w.id === targetWs)?.name || targetWs}"`
      : `dispatché automatiquement par la colonne Cabinet`;
    const tName = tables.find(t => t.id === tableId)?.name || tableId;
    if (!window.confirm(`Lancer l'import de la table "${tName}" ${wsLabel} ?\n\nLes dossiers existants seront mis à jour, les nouveaux créés.`)) return;
    setImporting(true);
    try {
      await api.post('/api/admin/platform-backup/import', {
        table_id: tableId,
        target_workspace_id: targetWs || null,
      });
      showToast('Import lancé en arrière-plan. Consultez les logs.');
    } catch(e) {
      showToast(e.response?.data?.detail || 'Erreur', 'error');
    } finally { setImporting(false); }
  };

  return (
    <div style={{ ...card, padding:'18px' }}>
      <div style={sectionTitle}>Import historique</div>
      <div style={{ fontSize:'11px', color:D.tx3, marginBottom:'14px' }}>
        Importe les dossiers d'une table Airtable Swap dans Lexback. Tu peux choisir une table spécifique et un workspace cible, ou laisser le dispatch automatique via la colonne Cabinet.
      </div>

      {loading ? (
        <div style={{ color:D.tx3, fontSize:'12px' }}>Chargement…</div>
      ) : (
        <>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px', marginBottom:'14px' }}>
            <div>
              <div style={lbl}>Table à importer</div>
              <select style={inp} value={tableId} onChange={e => setTableId(e.target.value)}>
                <option value="">— Choisir une table —</option>
                {tables.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <div style={lbl}>Workspace cible</div>
              <select style={inp} value={targetWs} onChange={e => setTargetWs(e.target.value)}>
                <option value="">Auto (via colonne Cabinet)</option>
                {workspaces.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
              </select>
            </div>
          </div>

          <div style={{ fontSize:'10px', color:D.tx3, marginBottom:'12px', lineHeight:1.5 }}>
            {targetWs
              ? 'Tous les dossiers de cette table iront dans le workspace sélectionné.'
              : 'Chaque dossier sera dispatché selon sa colonne Cabinet. Les cabinets inconnus créeront un nouveau workspace.'}
          </div>

          <button onClick={handleImport} disabled={importing || !tableId} style={{ ...btn, opacity: (importing || !tableId) ? 0.5 : 1 }}>
            {importing ? 'Démarrage…' : 'Lancer l\'import'}
          </button>
        </>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// SECTION 4 — Logs
// ═════════════════════════════════════════════════════════════════════════════

const EVENT_COLORS = {
  push_success: D.green,
  push_failed: D.red,
  import_started: D.blue,
  import_completed: D.green,
  import_failed: D.red,
  mapping_auto_created: D.amber,
};

function LogsSection() {
  const [logs, setLogs]     = useState([]);
  const [filter, setFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    const params = filter ? `?event_type=${filter}` : '';
    api.get(`/api/admin/platform-backup/logs${params}`)
      .then(r => setLogs(r.data || []))
      .catch(()=>{})
      .finally(()=>setLoading(false));
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div style={{ ...card, padding:'18px' }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
        <div style={sectionTitle}>Logs ({logs.length})</div>
        <div style={{ display:'flex', gap:'8px', alignItems:'center' }}>
          <select style={{ ...inpSm, width:'180px' }} value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="">Tous les events</option>
            <option value="push_success">push_success</option>
            <option value="push_failed">push_failed</option>
            <option value="import_started">import_started</option>
            <option value="import_completed">import_completed</option>
            <option value="import_failed">import_failed</option>
            <option value="mapping_auto_created">mapping_auto_created</option>
          </select>
          <button onClick={refresh} style={{ ...btnGh, padding:'5px 10px', fontSize:'10px' }}>↻</button>
        </div>
      </div>

      {loading ? (
        <div style={{ color:D.tx3, fontSize:'12px' }}>Chargement…</div>
      ) : logs.length === 0 ? (
        <div style={{ color:D.tx3, fontSize:'12px' }}>Aucun log pour l'instant.</div>
      ) : (
        <div style={{ border:`1px solid ${D.b1}`, borderRadius:'6px', overflow:'hidden', maxHeight:'500px', overflowY:'auto' }}>
          {logs.map(log => (
            <div key={log.id} style={{ display:'grid', gridTemplateColumns:'140px 160px 1fr', gap:'10px', padding:'8px 12px', borderBottom:`1px solid ${D.b1}`, fontSize:'11px', alignItems:'baseline' }}>
              <div style={{ color:D.tx3, fontFamily:'monospace', fontSize:'10px' }}>{new Date(log.created_at).toLocaleString('fr-FR')}</div>
              <div style={{ color: EVENT_COLORS[log.event_type] || D.tx2, fontWeight:500, fontSize:'10px' }}>{log.event_type}</div>
              <div style={{ color:D.tx2 }}>{log.message || '—'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
